import { styled } from '@mui/system';
import { Avatar, Box, Typography } from '@mui/material';
import { colors, fontFamilies } from '../../../../../../../constants/theme';

export const TestimonialContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive, theme }) => ({
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'center',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  opacity: isActive ? 1 : 0.4,
  width: '420px',
  height: '553px',
  padding: '40px',
  transform: isActive ? 'scale(1)' : 'scale(0.69)',
  transformOrigin: 'center',
  boxShadow: isActive
    ? `
  -4px -4px 17.8px 0px rgba(205, 214, 223, 0.15),
   4px  4px 15px   0px rgba(205, 214, 223, 0.25)
`
    : '0 4px 12px rgba(0,0,0,0.18)',
  borderRadius: '16px',
  margin: isActive ? '0' : 'auto 0',
  pointerEvents: isActive ? 'auto' : 'none',
  userSelect: isActive ? 'auto' : 'none',
  [theme.breakpoints.down('lg')]: {
    width: '315px',
    height: '390px',
    padding: '30px',
    transform: isActive ? 'scale(1)' : 'scale(0.69)',
  },
  [theme.breakpoints.down('md')]: {
    width: '252px',
    height: '315px',
    padding: '24px',
    transform: isActive ? 'scale(1)' : 'scale(0.69)',
  },
  [theme.breakpoints.down(768)]: {
    opacity: isActive ? 1 : 0,
  },
}));

export const TestimonialTextContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showGradient',
})(({ theme, showGradient }) => ({
  position: 'relative',
  marginTop: '20px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '60px',
    background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 1))',
    pointerEvents: 'none',
    zIndex: 1,
    opacity: showGradient ? 1 : 0,
    transition: 'opacity 0.3s ease',
  },
  [theme.breakpoints.down('lg')]: {
    '&::after': {
      height: '30px',
    },
  },
  [theme.breakpoints.down('md')]: {
    '&::after': {
      height: '25px',
    },
  },
}));

export const TestimonialTextScrollable = styled(Box)(({ theme }) => ({
  maxHeight: '300px',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  [theme.breakpoints.down('lg')]: {
    maxHeight: '180px',
  },
  [theme.breakpoints.down('md')]: {
    maxHeight: '140px',
  },
}));

export const TestimonialText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 500,
  color: colors.primary,
  whiteSpace: 'pre-line',
  fontSize: '20px',
  lineHeight: '24px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '15px',
    lineHeight: '18px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    lineHeight: '14.4px',
  },
}));

export const TestimonialBoldText = styled('span')(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 400,
  color: colors.primary,
  fontSize: '16px',
  lineHeight: '25px',
  letterSpacing: '-0.2px',
  marginTop: '10px',
  whiteSpace: 'pre-line',
  display: 'block',
  [theme.breakpoints.down('lg')]: {
    fontSize: '12px',
    lineHeight: '18px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '10px',
    lineHeight: '14.4px',
  },
}));

export const TestimonialAuthorSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'top',
})(({ theme, top }) => ({
  display: 'flex',
  alignItems: { xs: 'left', md: 'center' },
  justifyContent: 'space-between',
  flexDirection: { xs: 'column', md: 'row' },
  width: '100%',
  marginTop: top ? '0' : '30px',
  marginRight: '7px',
  [theme.breakpoints.down('lg')]: {
    marginTop: top ? '0' : '22px',
  },
  [theme.breakpoints.down('md')]: {
    marginTop: top ? '0' : '18px',
  },
}));

export const TestimonialAuthorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.down('lg')]: {
    gap: '12px',
  },
  [theme.breakpoints.down('md')]: {
    gap: '10px',
  },
}));

export const AuthorName = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#2c2c2c',
  lineHeight: '27px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    fontSize: '13.5px',
    lineHeight: '20px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '11px',
    lineHeight: '16px',
  },
}));

export const AuthorCredentials = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#666',
  marginTop: '5px',
  lineHeight: '21px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '9px',
    lineHeight: '16px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '7px',
    lineHeight: '13px',
  },
}));

export const QuotationIcon = styled('img')(({ theme }) => ({
  width: '61px',
  height: '49px',
  [theme.breakpoints.down('lg')]: {
    width: '45.75px',
    height: '36.75px',
  },
  [theme.breakpoints.down('md')]: {
    width: '37px',
    height: '29px',
  },
}));

export const AuthorAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})(({ theme, backgroundColor }) => ({
  width: 88,
  height: 88,
  fontFamily: fontFamilies?.quicksand || 'Quicksand, sans-serif',
  fontWeight: 700,
  fontSize: '26px',
  lineHeight: '26px',
  letterSpacing: '0px',
  color: colors.primary,
  backgroundColor: backgroundColor,
  [theme.breakpoints.down('lg')]: {
    width: 60,
    height: 60,
  },
  [theme.breakpoints.down('md')]: {
    width: 48,
    height: 48,
  },
}));
